<template>
  <h-fs-dialog
    :value="show"
    bottom
  >
    <v-card tile light>
      <v-toolbar
        flat
        max-height="56"
        color="grey lighten-4"
      >
        <v-btn
          icon
          color="primary"
          @click.stop="close()"
        >
          <v-icon v-text="icons.close" />
        </v-btn>
        <v-spacer />
        <v-toolbar-title v-text="title" />
        <v-spacer />
        <v-btn
          icon
          color="primary"
          :loading="printing"
          @click.stop="print()"
        >
          <v-icon v-text="icons.print" />
        </v-btn>
      </v-toolbar>
      <v-divider />
      <v-card-text>
        <v-simple-table>
          <tbody>
            <h-field :loading="loading"  title="cashier" :value="data.cashierName" />
            <h-field :loading="loading" title="date" :value="datetime" />
          </tbody>
        </v-simple-table>
        <v-simple-table v-if="config.total || config.tip || config.openOrder || config.guest || config.average">
          <tbody>
            <h-field v-if="config.total" :loading="loading" title="total" :value="data.total" currency />
            <h-field v-if="config.total" :loading="loading" title="quantity" :value="data.count" />
            <h-field v-if="config.tip" :loading="loading" title="tip" :value="data.tipTotal" currency />
            <h-field v-if="config.openOrder" :loading="loading" title="openOrders" :value="data.leftOpenOrders" />
            <h-field v-if="config.guest" :loading="loading" title="guests" :value="data.guests" />
            <h-field v-if="config.average" :loading="loading" title="guestAverage" :value="data.guestAverage" currency />
          </tbody>
        </v-simple-table>
        <v-simple-table v-if="config.inout || config.till">
          <tbody>
            <h-field v-if="config.inout" :loading="loading" title="cashIn" :value="data.in" currency />
            <h-field v-if="config.inout" :loading="loading" title="cashOut" :value="data.out" currency />
            <h-field v-if="config.till" :loading="loading" title="tillStart" :value="data.tillStart" currency />
            <h-field v-if="config.till" :loading="loading" title="tillEnd" :value="data.tillEnd" currency />
          </tbody>
        </v-simple-table>
        <v-simple-table v-if="showOffset">
          <tbody>
            <h-field :loading="loading" title="offsetCash" :value="data.cashOffset" currency />
            <h-field :loading="loading" title="offsetCard" :value="data.cardOffset" currency />
          </tbody>
        </v-simple-table>
        <template v-if="showPaymentMethods">
          <v-subheader
            class="text-subtitle-2"
            style="height: auto; margin-top: 100px"
            v-text="__('paymentMethod')"
          />
          <v-simple-table class="mt-0">
            <thead>
              <tr>
                <th></th>
                <th class="text-right">{{ __('total') }}</th>
                <th class="text-right">{{ __('tip') }}</th>
                <th class="text-right">{{ __('sum') }}</th>
              </tr>
            </thead>
            <tbody v-if="loading">
              <tr v-for="i in 2">
                <td><v-skeleton-loader type="text" /></td>
                <td><v-skeleton-loader type="text" /></td>
                <td><v-skeleton-loader type="text" /></td>
                <td><v-skeleton-loader type="text" /></td>
              </tr>
            </tbody>
            <tbody v-else>
              <h-payment-method
                v-for="total in data.totals"
                :key="total.paymentMethod"
                v-bind="total"
              />
            </tbody>
          </v-simple-table>
        </template>
      </v-card-text>
    </v-card>
  </h-fs-dialog>
</template>

<script>
  import { mapState, mapGetters } from 'vuex'
  import moment from 'moment'
  import I18n from '@/Mixins/I18n'
  import Printable from '@/Mixins/Printable'
  import Userable from '@/Mixins/Userable'
  import UsesIcons from '@/Mixins/UsesIcons'
  import CashControl from '@/Models/Static/Business/CashControlType'
  import FranceLog from '@/Models/Static/FiscalCountry/FranceLog'
  import HFsDialog from '@/Components/FullScreenDialog'
  import HField from './Field'
  import HPaymentMethod from './PaymentMethod'

  const DATE_INPUT_FORMAT = 'YYYY-MM-DD HH:mm:ss'

  export default {
    mixins: [
      I18n,
      Printable,
      Userable,
      UsesIcons,
    ],

    components: {
      HFsDialog,
      HField,
      HPaymentMethod,
    },

    data: () => ({
      CashControl,
      FranceLog,
      loading: true,
      data: {},
    }),

    computed: {
      ...mapState('cashControl/report', [
        'show',
        'type',
      ]),
      ...mapState('cashControl', [
        'turn',
      ]),
      ...mapGetters('rummage', [
        'business',
        'cashier',
        'ticket',
      ]),
      title () {
        return this.__(`report${this.type}`)
      },
      config () {
        return this.ticket('reportxz')
      },
      dateOutputFormat () {
        return this.type == 'X' ? 'L LTS' : 'L'
      },
      formattedDate () {
        return moment
          .utc(this.data.date, DATE_INPUT_FORMAT, true) // strict mode ON
          .local()
          .format(this.dateOutputFormat)
      },
      datetime () {
        return this.data.date ? this.formattedDate : ''
      },
      showPaymentMethods () {
        return this.config.paymentMethod && (! Array.isArray(this.data.totals) || this.data.totals.length > 0)
      },
      showOffset () {
        return this.config.cashOffset && ! this.cashier?.cash_machine_id && this.CashControl.isManual(this.business('cashControlType'))
      },
    },

    watch: {
      show (v) {
        if (v) {
          this.requestReport()
        }
      },
    },

    methods: {
      close () {
        this.$store.dispatch('cashControl/report/close')
      },
      requestReport () {
        this.loading = true
        this.$log(`Requesting Report${this.type}...`)
        this[`requestReport${this.type}`]()
          .then(r => {
            this.$log(`Fetched Report${this.type}`)
            this.data = r.data
          })
          .catch(e => this.$store.dispatch('xError', e))
          .finally(() => this.loading = false)
      },
      requestReportX () {
        return this.$api('reportXTurn', {
          id: this.turn?.id,
        })
      },
      requestReportZ () {
        return this.$api('reportZTurn', {
          cashier_id: this.cashier?.id,
          date: moment().utc().format('YYYY-MM-DD'),
        })
      },
      print () {
        const currency = v => Number(v || 0).toFixed(2)
        const left = JSON.stringify({align: 'left'})

        this.printing = true
        this.initPrinter()

        AndroidPrinter.separator()
        AndroidPrinter.text(this.business('legalName'), left)
        AndroidPrinter.text(this.business('name'), left)
        AndroidPrinter.separator('-')
        AndroidPrinter.text(this.title, left)
        AndroidPrinter.text2(this.__('cashier'), this.data.cashierName)
        AndroidPrinter.text2(this.__('employee'), this.user.name)
        if (this.data.turn_id !== null) {
          AndroidPrinter.text2(this.__('turn'), this.data.turn_id + "")
        }
        AndroidPrinter.text2(this.__('date'), this.datetime)
        AndroidPrinter.separator()
        if (this.config.total) {
          AndroidPrinter.text(this.__('payments'), left)
          AndroidPrinter.separator('-')
          if (this.showPaymentMethods) {
            this.data.totals.forEach(o => {
              AndroidPrinter.text2(this.$store.getters['rummage/paymentMethod'](o.paymentMethod)?.name ?? '', currency(o.total))
            })
            AndroidPrinter.separator('-')
          }
          AndroidPrinter.text2(this.__('total'), currency(this.data.total))
          AndroidPrinter.separator()

          AndroidPrinter.text(this.__('tip'), left)
          AndroidPrinter.separator('-')
          if (this.showPaymentMethods) {
            this.data.totals.forEach(o => {
              AndroidPrinter.text2(this.$store.getters['rummage/paymentMethod'](o.paymentMethod)?.name ?? '', currency(o.tipAmount))
            })
            AndroidPrinter.separator('-')
          }
          AndroidPrinter.text2(this.__('total'), currency(this.data.tipTotal))
          AndroidPrinter.separator()

          AndroidPrinter.text2(this.__('quantity'), this.data.count + "")
        }
        if (this.config.openOrder) {
          AndroidPrinter.text2(this.__('openOrders'), this.data.leftOpenOrders + "")
        }
        if (this.config.guest) {
          AndroidPrinter.text2(this.__('guests'), this.data.guests + "")
        }
        if (this.config.average) {
          AndroidPrinter.text2(this.__('guestAverage'), this.data.guestAverage + "")
        }
        if (this.config.inout && (this.data.in != 0 || this.data.out != 0)) {
          AndroidPrinter.separator('-')
          AndroidPrinter.text(this.__('cashInTitle'), left)
          this.data.inTotals.forEach(o => {
            AndroidPrinter.text2(this.$store.getters['rummage/paymentMethod'](o.payment_method_id)?.name ?? '', currency(o.amount))
          })
          AndroidPrinter.text(this.__('cashOutTitle'), left)
          this.data.outTotals.forEach(o => {
            AndroidPrinter.text2(this.$store.getters['rummage/paymentMethod'](o.payment_method_id)?.name ?? '', currency(o.amount))
          })
          AndroidPrinter.separator('-')
          AndroidPrinter.text(this.__('inOutTitle'), left)
          this.data.inouts.forEach(o => {
            AndroidPrinter.text2(o.reason, currency(o.amount))
          })
          AndroidPrinter.separator('-')
        }
        if (this.config.till) {
          AndroidPrinter.separator()
          AndroidPrinter.text(this.__('till'), left)
          AndroidPrinter.separator('-')
          AndroidPrinter.text2(this.__('tillStart'), currency(this.data.tillStart))
          AndroidPrinter.text2(this.__('tillEnd'), currency(this.data.tillEnd))
        }
        if (this.showOffset) {
          AndroidPrinter.separator()
          AndroidPrinter.text(this.__('offset'), left)
          AndroidPrinter.separator('-')
          AndroidPrinter.text2(this.__('offsetCash'), currency(this.data.cashOffset))
          AndroidPrinter.text2(this.__('offsetCard'), currency(this.data.cardOffset))
        }

        Bridge.interfaces.AndroidPrinter.print()
          .then(() => {
            this.$log('Printed', `Report${this.type}`)
            this.printing = false
          })
          .catch(() => {
            this.$log('Print error', `Report${this.type}`)
            this.$logFrance(this.FranceLog.PRINTER_NOT_AVAILABLE, `Report${this.type}`)
            this.$store.dispatch('alert/showError', this.__('printError'))
            this.printing = false
          })
          // the finally method does not work in the current Bridge version
      },
    },
  }
</script>

<style lang="scss" scoped>
  .v-data-table {
    margin: 2em 0;
  }
</style>
